// @ts-nocheck
import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Shield,
  LayoutDashboard,
  ScanLine,
  ClipboardList,
  FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import AreaUserMenu from '@/components/common/AreaUserMenu';
import ModuleGuard from '@/components/common/ModuleGuard';


const NAVY = '#002E62';

const navLinks = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    to: '/staff/dashboard',
    isActive: (path: string) =>
      path === '/staff' || path === '/staff/dashboard' || path === '/staff-dashboard',
  },
  {
    label: 'My events',
    icon: ClipboardList,
    to: '/staff/dashboard',
    isActive: (path: string) =>
      path.startsWith('/staff/events') ||
      path.startsWith('/staff/scoring') ||
      path.startsWith('/staff/tournaments'),
  },
  {
    label: 'Check-In Sheets',
    icon: FileSpreadsheet,
    to: '/staff/check-in-sheets',
    isActive: (path: string) => path.startsWith('/staff/check-in-sheets'),
  },
];

const laterLinks = [
  { label: 'Officials', icon: ScanLine },
  { label: 'Scorekeepers', icon: ClipboardList },
];


const StaffLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, user, signOut } = useAuth();

  const isAdmin = profile?.role === 'admin';
  const roleLabel = isAdmin ? 'Admin' : 'Staff';
  const roleVariant = isAdmin ? 'membership' : 'success';

  const displayName =
    profile?.name ||
    (user?.email ? user.email.split('@')[0] : 'Staff');

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      /* noop */
    }
    navigate('/login');
  };

  const renderNav = (variant: 'sidebar' | 'mobile') =>
    navLinks.map((link) => {
      const active = link.isActive(location.pathname);
      if (variant === 'sidebar') {
        return (
          <button
            key={link.label}
            onClick={() => navigate(link.to)}
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-left transition ${
              active
                ? 'bg-[#002E62]/10 text-[#002E62] font-semibold'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <link.icon className="w-4 h-4 shrink-0" />
            {link.label}
          </button>
        );
      }
      return (
        <button
          key={link.label}
          onClick={() => navigate(link.to)}
          className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition ${
            active
              ? 'bg-[#002E62]/10 text-[#002E62]'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <link.icon className="w-4 h-4" />
          {link.label}
        </button>
      );
    });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Top app bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/staff/dashboard')}
            className="flex items-center gap-3"
            aria-label="Staff Portal home"
          >
            <span
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${NAVY}1a` }}
            >
              <Shield className="h-5 w-5" style={{ color: NAVY }} />
            </span>
            <span className="text-left">
              <span className="flex items-center gap-2">
                <span
                  className="text-lg font-bold leading-tight"
                  style={{ color: NAVY }}
                >
                  Staff Portal
                </span>
                <Badge variant={roleVariant}>{roleLabel}</Badge>
              </span>
              <span className="hidden sm:block text-xs text-slate-500 leading-tight">
                Event Management System
              </span>
            </span>
          </button>

          <AreaUserMenu roleLabel={roleLabel} settingsPath="/staff/dashboard" />
        </div>

        {/* Mobile nav */}
        <nav className="lg:hidden border-t border-slate-100 px-2 overflow-x-auto">
          <div className="flex gap-1 py-2 min-w-max">{renderNav('mobile')}</div>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:block w-60 shrink-0 py-8 pr-6">
          <nav className="sticky top-24 space-y-1">
            {renderNav('sidebar')}
            <div className="pt-6">
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Later
              </p>
              {laterLinks.map((link) => (
                <div
                  key={link.label}
                  aria-disabled="true"
                  title="Coming soon"
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 cursor-not-allowed select-none"
                >
                  <link.icon className="w-4 h-4 shrink-0" />
                  {link.label}
                </div>
              ))}
            </div>
          </nav>
        </aside>


        {/* Main content */}
        <main className="flex-1 min-w-0 py-8 lg:pl-8 lg:border-l lg:border-slate-200">
          <ModuleGuard module="staff_portal" redirectTo="/">
            <Outlet />
          </ModuleGuard>
        </main>

      </div>
    </div>
  );
};

export default StaffLayout;
